import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { useMsal, useAccount } from '@azure/msal-react';
import { type Client } from '@microsoft/microsoft-graph-client';
import type { AppUser, UserRole, ListIds, DailySnapshot } from '@/models/types';
import { getGraphClient } from '@/graph/graphClient';
import {
  resolveAllListIds,
  getListItems,
} from '@/graph/sharePointService';
import { logAuthEvent, setAuditUser } from '@/graph/auditMiddleware';
import { captureDailySnapshot } from '@/graph/snapshotService';

interface AppContextValue {
  graphClient: Client | null;
  currentUser: AppUser | null;
  listIds: ListIds | null;
  isInitialized: boolean;
  initError: string | null;
  refreshListIds: () => Promise<void>;
}

const AppContext = createContext<AppContextValue>({
  graphClient: null,
  currentUser: null,
  listIds: null,
  isInitialized: false,
  initError: null,
  refreshListIds: async () => {},
});

export function useAppContext() {
  return useContext(AppContext);
}

interface AppProviderProps {
  children: ReactNode;
}

export function AppProvider({ children }: AppProviderProps) {
  const { instance, accounts } = useMsal();
  // Prefer the active account (set after redirect handling) and fall back to
  // the first account in the cache. This keeps `account` accurate even when
  // MSAL repopulates accounts after a redirect.
  const activeAccount = instance.getActiveAccount();
  const account =
    useAccount(activeAccount ?? accounts[0] ?? null) ??
    activeAccount ??
    accounts[0] ??
    null;
  const [graphClient, setGraphClient] = useState<Client | null>(null);
  const [currentUser, setCurrentUser] = useState<AppUser | null>(null);
  const [listIds, setListIds] = useState<ListIds | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);

  const refreshListIds = useCallback(async () => {
    if (!graphClient) return;
    const ids = await resolveAllListIds(graphClient);
    setListIds(ids);
  }, [graphClient]);

  useEffect(() => {
    if (!account) return;
    // Hard timeout: even if Graph API hangs, never leave user staring at spinner forever
    const initTimeout = setTimeout(() => {
      console.warn('[Prism] init timed out after 15s — letting UI render anyway');
      setIsInitialized(true);
    }, 15000);

    const init = async () => {
      try {
        console.info('[Prism] init starting for', account.username);
        const client = getGraphClient(instance, account);
        setGraphClient(client);

        // Determine user role from token claims
        const idTokenClaims = account.idTokenClaims as
          | Record<string, unknown>
          | undefined;
        const roles = (idTokenClaims?.roles as string[]) ?? [];
        const userEmail = account.username.toLowerCase();

        // Bootstrap admins: hardcoded fallback when App Roles aren't configured yet
        const BOOTSTRAP_ADMINS = ['pk@2sdtechnologies.com'];

        let role: UserRole = 'Member';
        if (roles.includes('Admin') || BOOTSTRAP_ADMINS.includes(userEmail)) {
          role = 'Admin';
        } else if (roles.includes('RO')) {
          role = 'RO';
        }

        setCurrentUser({
          id: account.localAccountId,
          displayName: account.name ?? account.username,
          email: account.username,
          role,
        });

        // Seed audit middleware with the signed-in user
        setAuditUser(account.username);

        // Resolve SharePoint site and list IDs
        try {
          const ids = await resolveAllListIds(client);
          setListIds(ids);

          // Log login event once SP is available
          // Use sessionStorage to avoid double-logging on StrictMode / hot reload
          const sessionKey = `prism_login_logged_${account.homeAccountId}`;
          if (!sessionStorage.getItem(sessionKey)) {
            void logAuthEvent(client, 'Login', account.username, `Role: ${role}`);
            sessionStorage.setItem(sessionKey, Date.now().toString());
          }

          // Auto-capture daily snapshot if today's doesn't exist yet.
          // Any authenticated user triggers it on first visit of the day — cheap if already exists.
          // We fire-and-forget so slow capture doesn't block the UI.
          void (async () => {
            try {
              const today = new Date().toISOString().split('T')[0];
              const snaps = await getListItems<DailySnapshot>(
                client,
                'OC_DailySnapshot',
                {
                  top: 30,
                  orderBy: 'fields/SnapshotDate desc',
                },
              );
              const alreadyToday = snaps.some(
                (s) => s.fields.SnapshotDate?.slice(0, 10) === today,
              );
              if (!alreadyToday) {
                await captureDailySnapshot(client, account.username);
                console.info('[Prism] Daily snapshot captured for', today);
              }
            } catch (err) {
              console.warn('[Prism] Daily snapshot capture failed:', err);
            }
          })();
        } catch (spError) {
          console.warn(
            'SharePoint lists not found. Run provisioning script.',
            spError,
          );
        }

        clearTimeout(initTimeout);
        console.info('[Prism] init complete');
        setIsInitialized(true);
      } catch (err) {
        clearTimeout(initTimeout);
        console.error('[Prism] init failed:', err);
        const message =
          err instanceof Error ? err.message : 'Failed to initialize';
        setInitError(message);
        setIsInitialized(true);
      }
    };

    init();
    return () => clearTimeout(initTimeout);
    // Use account.homeAccountId so a stable token refresh doesn't re-fire init
  }, [account?.homeAccountId, instance]);

  return (
    <AppContext.Provider
      value={{
        graphClient,
        currentUser,
        listIds,
        isInitialized,
        initError,
        refreshListIds,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}
